import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/common/Page";
import { money, InvBadge } from "@/pages/invoices/invoiceConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Download, Settings as SettingsIcon } from "lucide-react";

const Card = ({ label, value, tone = "text-slate-100", testId }) => (
  <div data-testid={testId} className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
    <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`mt-1.5 text-xl font-bold ${tone}`}>{value}</div>
  </div>
);

export default function InvoicesDashboard() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const { data: dash } = useQuery({ queryKey: ["inv-dashboard"], queryFn: async () => (await api.get("/invoices/dashboard")).data });
  const { data: rows } = useQuery({ queryKey: ["invoices"], queryFn: async () => (await api.get("/invoices")).data });

  const filtered = (rows || []).filter((r) => {
    const label = r.overdue ? "Overdue" : r.status;
    if (status !== "all" && label !== status) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return r.number.toLowerCase().includes(s) || (r.bill_to?.legal_name || "").toLowerCase().includes(s) || (r.bill_to?.abn || "").toLowerCase().includes(s);
  });
  const c = dash?.cards || {};

  return (
    <div data-testid="invoices-dashboard">
      <PageHeader title="Invoices" subtitle="Accounts receivable — invoicing, payments and GST.">
        <Button variant="outline" className="border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]" onClick={async () => { const r = await api.get("/invoices/export", { responseType: "blob" }); const u = URL.createObjectURL(new Blob([r.data])); const a = document.createElement("a"); a.href = u; a.download = "invoice_register.csv"; a.click(); setTimeout(() => URL.revokeObjectURL(u), 60000); }} data-testid="invoice-export-btn"><Download className="mr-1.5 h-4 w-4" /> CSV</Button>
        <Button variant="outline" className="border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]" onClick={() => navigate("/invoices/settings")} data-testid="invoice-settings-btn"><SettingsIcon className="mr-1.5 h-4 w-4" /> Settings</Button>
        <Button onClick={() => navigate("/invoices/new")} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="new-invoice-btn"><Plus className="mr-1.5 h-4 w-4" /> New Invoice</Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
        <Card label="Outstanding" value={money(dash?.outstanding_cents)} tone="text-amber-300" testId="card-outstanding" />
        <Card label="Overdue" value={money(dash?.overdue_cents)} tone="text-rose-300" testId="card-overdue" />
        <Card label="Collected (mo)" value={money(dash?.collected_month_cents)} tone="text-emerald-300" testId="card-collected" />
        <Card label="Draft" value={c.Draft || 0} testId="card-draft" />
        <Card label="Sent" value={c.Sent || 0} />
        <Card label="Paid" value={c.Paid || 0} tone="text-emerald-300" />
      </div>

      {dash?.ageing && (
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="ageing">
          {[["current", "Current"], ["1-30", "1–30d"], ["31-60", "31–60d"], ["61-90", "61–90d"], ["90+", "90+d"]].map(([k, l]) => (
            <Card key={k} label={`AR ${l}`} value={money(dash.ageing[k])} tone="text-slate-300" />
          ))}
        </div>
      )}

      <div className="mb-4 mt-6 flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number, customer, ABN…" className="border-[#2D3F62] bg-[#0B1120] pl-9 text-slate-100" data-testid="invoice-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48 border-[#2D3F62] bg-[#0B1120] text-slate-100" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
            {["all", "Draft", "Approved", "Issued", "Sent", "Partially Paid", "Paid", "Overdue", "Void"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[#1E2D4A]">
        <table className="w-full text-sm">
          <thead className="bg-[#0E1729] text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr>{["Number", "Customer", "Issue", "Due", "Total", "Paid", "Balance", "Status"].map((h) => <th key={h} className="whitespace-nowrap px-3 py-3">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[#1E2D4A]">
            {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-500" data-testid="no-invoices">No invoices yet.</td></tr>}
            {filtered.map((r) => (
              <tr key={r.id} className="cursor-pointer bg-[#111A2E]/40 hover:bg-[#17233B]/60" onClick={() => navigate(`/invoices/${r.id}`)} data-testid={`invoice-row-${r.id}`}>
                <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-cyan-300">{r.number}</td>
                <td className="px-3 py-2.5 text-slate-100">{r.bill_to?.legal_name}</td>
                <td className="whitespace-nowrap px-3 py-2.5 text-slate-400">{r.issue_date || "—"}</td>
                <td className="whitespace-nowrap px-3 py-2.5 text-slate-400">{r.due_date || "—"}</td>
                <td className="px-3 py-2.5 text-slate-200">{money(r.total_cents)}</td>
                <td className="px-3 py-2.5 text-slate-400">{money(r.amount_paid_cents)}</td>
                <td className="px-3 py-2.5 text-slate-200">{money(r.balance_cents)}</td>
                <td className="px-3 py-2.5"><InvBadge inv={r} testId={`invoice-status-${r.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
